import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Slack, Send, Trash2, Check, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

/**
 * QENVA · Slack Channel Settings.
 *
 * Workspace-level configuration of a Slack incoming webhook used as a SECONDARY
 * channel for the Daily Brief. Independent from email; failure of one does
 * not affect the other.
 *
 * Endpoints:
 *   GET    /api/workspaces/{id}/slack-webhook        — masked status
 *   PUT    /api/workspaces/{id}/slack-webhook        — set/update (admin+)
 *   POST   /api/workspaces/{id}/slack-webhook/test   — send a "hello" message
 *   DELETE /api/workspaces/{id}/slack-webhook        — remove entirely
 *
 * The raw webhook URL is NEVER returned from the backend — only `webhook_masked`.
 */
export default function SlackChannelSection({ workspaceId }) {
  const { t } = useTranslation("common");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [showInput, setShowInput] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${workspaceId}/slack-webhook`);
      setStatus(r.data);
      setShowInput(!r.data?.configured);
    } catch (e) {
      // Non-blocking — silently keep empty state
      setStatus({ configured: false, enabled: false });
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const save = async (enabled) => {
    setSaving(true);
    try {
      const r = await api.put(`/workspaces/${workspaceId}/slack-webhook`, {
        webhook_url: newUrl || status?.webhook_masked || "",
        enabled: enabled,
      });
      setStatus(r.data);
      setNewUrl("");
      setShowInput(false);
      toast.success(t("settings.slack.saved", { defaultValue: "Webhook Slack enregistré" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (checked) => {
    if (!status?.configured) {
      toast.error(t("settings.slack.configure_first", {
        defaultValue: "Configurez d'abord un webhook avant d'activer",
      }));
      return;
    }
    setSaving(true);
    try {
      // We can't re-save the URL (server doesn't return it), so we send a
      // sentinel that matches the existing URL — backend doesn't accept this,
      // so instead we ONLY support toggling via a dedicated endpoint:
      // simplest is to require re-paste when toggling from disabled to enabled.
      // Here we use the masked URL only for display; the backend has the real
      // one, so we just need to re-PUT with the SAME masked value to keep it.
      // To avoid this complexity, we expose a tiny PATCH-like path: re-PUT
      // requires a valid URL, so we just toggle via the test-then-PUT loop.
      // Simplest UX: when toggling without re-entering, we use the masked
      // value which fails server validation — so we force the input flow.
      if (!checked) {
        // Disabling is easy: PUT with current (we don't have it) → workaround:
        // call DELETE then PUT, OR add a "disabled" toggle on the existing
        // backend. Simplest here: only disable via DELETE.
        await api.delete(`/workspaces/${workspaceId}/slack-webhook`);
        setStatus({ configured: false, enabled: false });
        toast.success(t("settings.slack.disabled", { defaultValue: "Canal Slack désactivé" }));
      } else {
        setShowInput(true);
        toast.info(t("settings.slack.re_enter_url", {
          defaultValue: "Re-saisissez le webhook pour réactiver",
        }));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    } finally {
      setSaving(false);
    }
  };

  const testWebhook = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/workspaces/${workspaceId}/slack-webhook/test`);
      if (r.data?.status === "ok") {
        toast.success(t("settings.slack.test_ok", {
          defaultValue: "Message de test envoyé sur Slack ✓",
        }));
      } else {
        toast.error(t("settings.slack.test_failed", {
          status: r.data?.http_status || "?",
          defaultValue: `Échec de l'envoi Slack (HTTP ${r.data?.http_status || "?"})`,
        }));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec du test");
    } finally {
      setTesting(false);
    }
  };

  const removeWebhook = async () => {
    if (!window.confirm(t("settings.slack.confirm_remove", {
      defaultValue: "Retirer la configuration Slack ?",
    }))) return;
    setRemoving(true);
    try {
      await api.delete(`/workspaces/${workspaceId}/slack-webhook`);
      setStatus({ configured: false, enabled: false });
      setNewUrl("");
      setShowInput(true);
      toast.success(t("settings.slack.removed", { defaultValue: "Webhook Slack retiré" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <section
        className="border border-zinc-200 rounded-2xl bg-white p-6 mb-6"
        data-testid="slack-section-loading"
      >
        <Loader2 className="animate-spin text-zinc-400" />
      </section>
    );
  }

  return (
    <section
      className="border border-zinc-200 rounded-2xl bg-white p-6 mb-6"
      data-testid="slack-section"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-violet-50 text-violet-700">
            <Slack size={18} strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-zinc-950">
              {t("settings.slack.title", { defaultValue: "Canal Slack du workspace" })}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5 max-w-md">
              {t("settings.slack.subtitle", {
                defaultValue: "Envoie le Daily Brief sur le canal Slack de votre équipe en plus de l'email.",
              })}
            </p>
          </div>
        </div>
        {status?.configured && (
          <div className="flex items-center gap-2" data-testid="slack-enabled-toggle-wrap">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
              {status.enabled ? t("settings.slack.active", { defaultValue: "Actif" }) : t("settings.slack.inactive", { defaultValue: "Inactif" })}
            </span>
            <Switch
              checked={!!status.enabled}
              onCheckedChange={toggleEnabled}
              disabled={saving}
              data-testid="slack-enabled-toggle"
            />
          </div>
        )}
      </div>

      {status?.configured ? (
        <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 mb-3" data-testid="slack-current-config">
          <div className="flex items-center gap-2 text-[12px] text-zinc-700">
            <Check className="text-emerald-600" size={14} />
            <span className="font-medium">Webhook configuré :</span>
            <code className="text-zinc-500 font-mono text-[11px]" data-testid="slack-webhook-masked">
              {status.webhook_masked}
            </code>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={testWebhook}
              disabled={testing}
              data-testid="slack-test-button"
            >
              {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              <span className="ml-1.5">{t("settings.slack.test", { defaultValue: "Tester l'envoi" })}</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={removeWebhook}
              disabled={removing}
              className="text-rose-700 border-rose-200 hover:bg-rose-50"
              data-testid="slack-remove-button"
            >
              {removing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              <span className="ml-1.5">{t("settings.slack.remove", { defaultValue: "Retirer" })}</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-3 text-[12px] text-amber-900 flex items-start gap-2" data-testid="slack-not-configured">
          <AlertCircle size={14} className="mt-0.5" />
          <span>
            {t("settings.slack.not_configured", {
              defaultValue: "Aucun webhook Slack configuré pour ce workspace.",
            })}
          </span>
        </div>
      )}

      {showInput && (
        <div className="space-y-3" data-testid="slack-input-block">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
              {t("settings.slack.webhook_url_label", { defaultValue: "URL du webhook Slack" })}
            </label>
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://hooks.slack.com/services/..."
              className="mt-1 w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 font-mono"
              data-testid="slack-webhook-input"
            />
            <p className="mt-1.5 text-[11px] text-zinc-500">
              {t("settings.slack.help_create_webhook", {
                defaultValue: "Créez un webhook entrant sur",
              })}{" "}
              <a
                href="https://api.slack.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-700 hover:text-violet-950 inline-flex items-center gap-0.5"
              >
                api.slack.com/apps <ExternalLink size={9} />
              </a>
              {t("settings.slack.help_paste_url", {
                defaultValue: " et collez l'URL ici.",
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => save(true)}
              disabled={saving || !newUrl.startsWith("https://hooks.slack.com/")}
              data-testid="slack-save-button"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              <span className="ml-1.5">
                {t("settings.slack.save_and_enable", { defaultValue: "Enregistrer & activer" })}
              </span>
            </Button>
            {status?.configured && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setShowInput(false); setNewUrl(""); }}
                data-testid="slack-cancel-button"
              >
                {t("common.cancel", { defaultValue: "Annuler" })}
              </Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
